import { FilterFieldType } from './../../../covance-filter/covance-filter.models';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FilterFieldValue, CovanceFilter } from '../../../covance-filter';
import { Category, CategoriesStorage, Report } from '../../../documents';
import { SponsorStudy } from '../../../sponsor-dashboard/sponsor-study';
import { SponsorRouteData } from '../../sponsor-route-data';
import { SponsorReportsStorage } from '../../sponsor-reports-storage';
import { Settings } from '../../../core';
import { distinctUntilChanged, filter, flatMap, map, switchMap, publishReplay, refCount } from 'rxjs/operators';
import { SponsorDocumentsFilterItemsStorage } from '../sponsor-documents-filter-items-storage';
import { isEmpty } from 'lodash';

@Component({
    selector: 'sponsor-lab-reports',
    template: require('./sponsor-lab-reports.component.html'),
    styles: [require('./sponsor-lab-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SponsorLabReportsComponent {

    public showLargeDataWarning: Observable<boolean>;
    public warningThreshhold: number;
    public reports$: Observable<Report[]>;
    public filterFields$: Observable<FilterFieldValue[]>;

    public dataSwitch = new Subject<boolean>();

    public selectedCategory$: Observable<Category>;
    public selectedStudy$: Observable<SponsorStudy>;
    public protocolId$: Observable<number>;

    constructor(
        private readonly routeData: SponsorRouteData,
        private readonly reportsStorage: SponsorReportsStorage,
        private readonly categoriesStorage: CategoriesStorage,
        private readonly filterItemsStorage: SponsorDocumentsFilterItemsStorage,
        private readonly filterService: CovanceFilter,
        private readonly settings: Settings
    ) {
     }

    public ngOnInit() {

        this.selectedStudy$ = this.routeData.selectedStudy.pipe(
            filter(x => !!x),
            distinctUntilChanged()
        ); 

        this.protocolId$ = this.selectedStudy$.pipe(
            map(study => study.protocolId)
        );

        this.warningThreshhold = this.settings.maxNumberOfSponsorLabReports;

        this.selectedCategory$ = this.categoriesStorage.findCategoryById('lab-reports');

        this.filterFields$ =  this.selectedCategory$.pipe(
            flatMap(category => this.filterItemsStorage.loadFilterItemsByCategory(category)),
            map(filterItems => {
                const filterDefs: FilterFieldValue[] = [
                    { def: {
                        title: 'Type',
                        queryParam: 'type',
                        isServerSideFilter: true,
                        type: FilterFieldType.MultiSelect,                  
                    }},
                    { def: {
                            title: 'Site',
                            queryParam: 'site',
                            isServerSideFilter: true,
                            type: FilterFieldType.MultiSelect,
                    }},
                    { def: {
                            title: 'Visit',
                            queryParam: 'visit',
                            isServerSideFilter: true,
                            type: FilterFieldType.MultiSelect,
                    }},
                    { def: {
                        title: 'Subject',
                        queryParam: 'subject',
                        isServerSideFilter: true,
                        type: FilterFieldType.MultiSelect
                    }},
                    { def: {
                        title: 'Accession',
                        queryParam: 'accessionNumber',
                        isServerSideFilter: true,
                        type: FilterFieldType.MultiSelect
                    }},
                    { def: {
                        title: 'Posted',
                        queryParam: 'date',
                        isServerSideFilter: true,
                        type: FilterFieldType.DateRange
                       }
                    }
                ];
                this.filterService.setFiltersItems(filterItems, filterDefs);
                return filterDefs;
            }),
            publishReplay(1),
            refCount());

        const filterFlow$ = this.filterFields$
        .pipe(
            switchMap(fields => this.filterService.listen(fields).pipe(map(() => fields))),
            publishReplay(1),
            refCount()
        );

        this.reports$ = filterFlow$
            .pipe(
                switchMap((fields) => {
                    this.selectedCategory$ = this.categoriesStorage.findCategoryById('lab-reports');
          
                    return this.reportsStorage.loadStudyReports(
                        this.selectedStudy$,
                        this.selectedCategory$,
                        fields
                    );
                }),
                publishReplay(1),
                refCount()
            );
            
        this.showLargeDataWarning = this.reports$.pipe(
            map((data: []) => !isEmpty(data) && this.warningThreshhold > 0 && data.length >= this.warningThreshhold)
        );
    }
}
